import { useEffect, useRef, useState } from "react";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpFloService from "@plannel/services/fp/fp-flo-service";
import FlowChart from "@plannel/pages/factory-plan/common/component/FlowChart";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import InventoryPopup from "@plannel/pages/factory-plan/common/popup/InventoryPopup";
import { floDataToFlow } from "@plannel/pages/factory-plan/common/flowchart";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

/**
 * FLO — 인벤토리 1건을 기준으로 BOM 정방향 전개(공정/BOR/자원/공구/부산물/자재)를 플로우차트로
 * 보여주는 조회 전용 화면. old `view/factoryplan/master/flo/Flo.jsx` 이관.
 *
 * 트리 조립·엣지 라벨·좌표 배치는 전부 `common/flowchart.js`(공용 함수), 렌더링은
 * `common/component/FlowChart.js`(래퍼) + `FlowNode.js`(도형)에 있다 — BomDiagram/
 * ByproductDiagram/OrderTrackingDiagram이 같은 엔진을 공유할 수 있게 화면 밖으로 뺐다.
 */
const Flo = ({ t, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [inventoryCd, setInventoryCd] = useState(reduxViewState?.inventoryCd ?? "");

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const [inventoryPopupOpen, setInventoryPopupOpen] = useState(false);
  // 지우기(Clear) 아이콘은 포커스 중 + 값이 있을 때만 노출(ItemHrchyTreeFilter와 동일 조건).
  const [inventoryFocused, setInventoryFocused] = useState(false);
  const [progressOn, setProgressOn] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  // 조회 실행 신호 — 이 화면은 planScope 변경만으로 자동 조회하지 않는다(old와 동일하게
  // 인벤토리를 고르고 검색을 눌러야 조회). 값 변경과 조회 시점을 분리하려고 카운터를 쓴다.
  // 단, **조회 팝업에서 더블클릭으로 고른 경우는 그 자체가 확정 액션**이라 바로 조회한다(아래 InventoryPopup).
  const [searchSeq, setSearchSeq] = useState(0);
  // 조회 요청 토큰(§0-8) — 인벤토리를 빠르게 바꾸면 이전 응답이 나중에 도착해 최신 결과를 덮는다.
  const latestReqRef = useRef(0);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, inventoryCd });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, inventoryCd]);

  // 탭 재진입/복원 시 1회 자동 조회 — 다른 화면들은 planScope만 있으면 들어오자마자 결과가
  // 보이는데, 이 화면은 조회에 인벤토리가 필수라 "값은 복원됐는데 화면은 비어 있는" 상태가 됐다.
  // 저장된 조회조건이 남아 있으면 그대로 한 번 조회해준다. planScope는 PlanScope 컴포넌트가
  // 목록 로드 후 채워주므로 그 시점을 기다린다(그래서 의존성은 planScope 하나).
  // 판정은 최초 1회뿐이다 — 이후 planScope를 바꾸는 것만으로는 자동 조회하지 않는다(§14-4).
  const initialSearchRef = useRef(false);
  useEffect(() => {
    if (initialSearchRef.current || !planScope) return;
    initialSearchRef.current = true;
    if (inventoryCd) setSearchSeq((seq) => seq + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  // 딥링크 수신(fp-master-screen-guide.md §13-2). 이 화면은 조회조건이 바뀌어도 자동 재조회하지
  // 않으므로(위 searchSeq 주석), 값 반영 뒤 조회를 명시적으로 트리거한다.
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    if (freshState.inventoryCd) setInventoryCd(freshState.inventoryCd);
    if (freshState.planScope && freshState.inventoryCd) setSearchSeq((seq) => seq + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const retrieveFlo = () => {
    const reqId = ++latestReqRef.current;
    setProgressOn(true);

    // old와 동일한 조회 파라미터 — bomTpCd 'P'(생산 BOM) 고정, 인벤토리 1건 기준 정방향 전개.
    fpFloService.getFlo({ viewId: viewName, subId: "", planScope, inventoryCd, bomTpCd: "P" })
      .then((response) => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setNodes([]);
          setEdges([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }
        // 응답 배열 = 선택 인벤토리를 만드는 공정별 트리(대체 공정). 예전엔 [0]만 그려서 나머지를 버렸다.
        const flow = floDataToFlow(results, t);
        setNodes(flow.nodes);
        setEdges(flow.edges);
      })
      .catch((e) => {
        debug(e);
        if (reqId !== latestReqRef.current) return;
        setNodes([]);
        setEdges([]);
      })
      .then(() => {
        if (reqId === latestReqRef.current) setProgressOn(false);
      });
  };

  useEffect(() => {
    if (searchSeq === 0) return;
    retrieveFlo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSeq]);

  const onSearch = () => {
    // old는 인벤토리를 필수(rules.required)로 검증했다 — 없으면 조회하지 않고 안내만 한다.
    if (!planScope || !inventoryCd) {
      setSnackInfo({
        open: true, severity: snackBarMessageColor.inputError,
        content: t("MSG_PleaseEnterValues", { values: t("FP_INVENTORY") })
      });
      return;
    }
    setSearchSeq((seq) => seq + 1);
  };

  // 노드 더블클릭 → 해당 기준정보 화면으로 딥링크 이동(§13-1). old는 history.push({pathname,state}).
  const onNodeDoubleClick = (event, node) => {
    const nav = node?.data?.nav;
    if (!nav?.reduxKey) return;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(nav.reduxKey, nav.fields)));
    reduxDispatch(reduxUtil.updateHistoryState(nav.reduxKey));
  };

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        {/* 조회조건 픽커 필드 — 비FP 모듈의 표준 필터 픽커(`components/filter/ItemHrchyTreeFilter.js`)와
            같은 구성으로 맞춘다: 타이핑 가능 + 값이 있을 때 지우기(Clear) + 목록 여는 화살표.
            ⚠️ 돋보기(🔍)는 이 코드베이스에서 **조회 실행** 아이콘이다(`ItemAutocomplete.js`의
            `onClick={retrieve}`, `LayoutListModal.js`의 `onClick={onSearch}`) — 목록/선택 UI를 여는
            자리에는 쓰지 않는다. 여는 대상이 Popover(다른 모듈)냐 그리드 팝업(FP)이냐만 다르다. */}
        <TextField
          label={t("FP_INVENTORY")}
          value={inventoryCd}
          onChange={(e) => setInventoryCd(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
          onFocus={() => setInventoryFocused(true)}
          onBlur={() => setInventoryFocused(false)}
          size="small"
          autoComplete="off"
          sx={{ ml: 1.5, minWidth: 260 }}
          InputProps={{
            endAdornment: (
              <>
                {inventoryFocused && inventoryCd && (
                  <IconButton
                    size="small"
                    aria-label="clear text"
                    // 버튼을 누르는 순간 blur가 먼저 일어나 버튼이 사라지는 것을 막는다.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setInventoryCd("")}
                  >
                    <ClearIcon sx={{ fontSize: "1.2rem" }} />
                  </IconButton>
                )}
                <InputAdornment position="end">
                  {/* 조회 팝업 트리거 아이콘 = `…`(MoreHoriz). old FP(RealGrid `button:"action"`)의 조회
                      팝업 버튼과 같은 모양이라 기존 사용자에게 학습 비용이 없고, new 코드베이스에서
                      이 아이콘이 다른 의미로 쓰이는 곳도 없다. 화살표(▼)는 필드 안에서 펼쳐지는
                      목록(Popover/Select) 뜻이라 모달이 뜨는 이 동작과 어긋나서 쓰지 않는다. */}
                  <IconButton size="small" onClick={() => setInventoryPopupOpen(true)} disabled={!planScope}>
                    <MoreHorizIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </InputAdornment>
              </>
            )
          }}
        />
        {/* 조회는 인벤토리 입력칸의 Enter(또는 조회 팝업 더블클릭)로만 트리거한다 — 별도 검색 버튼은 두지 않는다.
            버튼 그룹이 없는 화면이라 빈 Box를 마지막 자식으로 남긴다(§6 — 안 그러면 인벤토리 입력칸이
            float:right로 밀려 PlanScope와 멀어진다). */}
        <Box />
      </FilterContainer>
      <Box id="fp-flo-diagram" className="grid-area" sx={{ minHeight: 0 }} mb={1}>
        <FlowChart nodes={nodes} edges={edges} onNodeDoubleClick={onNodeDoubleClick} />
      </Box>
      <InventoryPopup
        open={inventoryPopupOpen}
        onClose={() => setInventoryPopupOpen(false)}
        planScope={planScope}
        onSelect={(row) => {
          // 팝업에서 더블클릭으로 고르면 팝업이 닫히면서(InventoryPopup 내부) 곧바로 조회까지 간다 —
          // 다른 화면의 코드픽커 선택(선택 = 값 확정)과 트리거를 통일한 것. 값 반영과 조회 시점은
          // 여전히 분리돼 있어(searchSeq) 직접 타이핑할 때는 검색 버튼/Enter가 필요하다.
          setInventoryCd(row.inventoryCd);
          setSearchSeq((seq) => seq + 1);
        }}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Flo);
